import { z } from 'zod';

import { UuidSchema } from '../../common/id/id';
import { isMutualCandidate, LikeDecisionSchema } from '../like-decision/like-decision';

/**
 * What the member is told immediately after deciding.
 *
 * `matched` is the whole member-facing point of the feature — it is the moment
 * the app celebrates — so it is answered **in the response** rather than
 * arriving later on a socket or a push. That is why the match is created in the
 * request path and not by a listener: a fire-and-forget listener cannot answer
 * the request that emitted it (P14a).
 *
 * The two refinements pin the invariant rather than trusting the mapper:
 * `matched` and `matchId` are **one fact with two spellings**, and a `matched`
 * with no id would send a client to a conversation it cannot name. A pass can
 * never carry either (`isMutualCandidate`), so the day a third decision is added
 * this contract refuses it until somebody decides what it means.
 */
export const LikeResultSchema = z
  .object({
    decision: LikeDecisionSchema,
    matched: z.boolean(),
    matchId: UuidSchema.nullable(),
  })
  .strict()
  .refine((result) => result.matched === (result.matchId !== null), {
    message: '`matched` and `matchId` must agree — a match is one fact, not two.',
    path: ['matchId'],
  })
  .refine((result) => !result.matched || isMutualCandidate(result.decision), {
    message: 'Only a like can complete a match.',
    path: ['matched'],
  });

export type LikeResult = z.infer<typeof LikeResultSchema>;
